import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, setToken, formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading, false = logged out
  const [activeProperty, setActiveProperty] = useState(null);
  // null = not yet loaded, [] = loaded with zero properties.
  // OnboardingGate must distinguish these to avoid bouncing freshly-logged-in
  // users to /onboarding before /api/properties resolves.
  const [properties, setProperties] = useState(null);

  const loadMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch {
      setUser(false);
      return null;
    }
  }, []);

  const loadProperties = useCallback(async () => {
    try {
      const { data } = await api.get("/properties");
      setProperties(data || []);
      return data || [];
    } catch {
      setProperties([]);
      return [];
    }
  }, []);

  useEffect(() => {
    loadMe();
  }, [loadMe]);

  useEffect(() => {
    if (user && user !== false) {
      loadProperties().then((props) => {
        const safeProps = Array.isArray(props) ? props.filter(Boolean) : [];
        const activeId = user.active_property_id || safeProps[0]?.id;
        const found = safeProps.find((p) => p && p.id === activeId);
        setActiveProperty(found || safeProps[0] || null);
      });
    }
  }, [user, loadProperties]);

  const login = async (email, password) => {
    // Clear any stale token before requesting login so the Authorization
    // header doesn't get attached with an expired bearer.
    setToken(null);
    const { data } = await api.post("/auth/login", { email, password });
    setToken(data.access_token);
    // Reset properties/activeProperty to the "loading" state (null)
    // BEFORE flipping `user` truthy — otherwise OnboardingGate reads
    // the previous session's `properties === []` and bounces the
    // freshly-logged-in user to /onboarding for a split second before
    // /api/properties resolves.
    setProperties(null);
    setActiveProperty(null);
    setUser(data.user);
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    setToken(data.access_token);
    setProperties(null);
    setActiveProperty(null);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {}
    setToken(null);
    setUser(false);
    setActiveProperty(null);
    // Reset to `null` (loading), not `[]`. The next login flips `user`
    // truthy before /api/properties re-resolves; the OnboardingGate
    // gate reads `properties !== null && length === 0` and would
    // wrongly redirect to /onboarding if we left the empty array here.
    setProperties(null);
  };

  const switchProperty = async (pid) => {
    if (!pid) return;
    await api.post(`/properties/${pid}/activate`);
    const prop = (properties || []).find((p) => p && p.id === pid);
    if (prop) setActiveProperty(prop);
    setUser((u) => (u ? { ...u, active_property_id: pid } : u));
  };

  const refreshProperties = async () => {
    const props = await loadProperties();
    if (!activeProperty && props.length) setActiveProperty(props[0]);
    return props;
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser,
        properties,
        activeProperty,
        setActiveProperty,
        login,
        register,
        logout,
        switchProperty,
        refreshProperties,
        formatApiError,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
